import React, { useState } from 'react';
import { Sparkles, Send } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AIPromptInputProps {
  onSubmit: (prompt: string) => void;
  placeholder?: string;
  className?: string;
}

/**
 * AIPromptInput - AI-powered prompt input field
 * 
 * Replaces traditional "Add/Create" buttons with AI-first interaction.
 * User describes what they want in natural language, and AI takes action.
 */
export function AIPromptInput({ 
  onSubmit, 
  placeholder = "Ask Themis to help...",
  className 
}: AIPromptInputProps) {
  const [prompt, setPrompt] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (prompt.trim()) {
      onSubmit(prompt.trim());
      setPrompt('');
    }
  };

  return (
    <form 
      onSubmit={handleSubmit}
      className={cn(
        "relative flex items-center gap-2 transition-all duration-200",
        className
      )}
    >
      {/* AI Sparkle Icon Watermark */}
      <div className={cn(
        "absolute left-3 flex items-center justify-center transition-colors duration-200",
        isFocused ? "text-primary-500" : "text-neutral-400"
      )}>
        <Sparkles className="w-4 h-4" />
      </div>

      {/* Prompt Input */}
      <input
        type="text"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        className={cn(
          "flex-1 pl-10 pr-12 py-2.5 rounded-lg",
          "bg-neutral-900 border-2 transition-all duration-200",
          "text-white placeholder-neutral-500",
          "focus:outline-none focus:ring-0",
          isFocused 
            ? "border-primary-500 bg-neutral-900/80 shadow-lg shadow-primary-500/20" 
            : "border-neutral-700 hover:border-neutral-600"
        )}
      />

      {/* Send Button */}
      <button
        type="submit"
        disabled={!prompt.trim()}
        className={cn(
          "absolute right-2 p-2 rounded-md transition-all duration-200",
          prompt.trim()
            ? "text-primary-500 hover:bg-primary-500/10 cursor-pointer"
            : "text-neutral-600 cursor-not-allowed"
        )}
      >
        <Send className="w-4 h-4" />
      </button>
    </form>
  );
}
